import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Chrome, Globe, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { extractTextFromFile } from '@/lib/cvExtractor';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  onUpload?: (name: string, content: string) => Promise<void>;
  onPasteStart?: () => void;
}

export function OnboardingModal({ open, onClose, onComplete, onUpload }: OnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [baseCV, setBaseCV] = useState<File | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBaseCV(file);
      if (onUpload) {
        try {
          const text = await extractTextFromFile(file);
          
          if (!text || text.trim().length === 0) {
            toast.error("Could not extract text from this PDF. Please try a .docx file instead or paste your CV text manually");
            return;
          }
          
          await onUpload(file.name, text);
        } catch (err: any) {
          console.error('File read error:', err);
          toast.error(err.message || "Failed to process CV");
        }
      }
      setStep(2);
    }
  };

  const handleSkip = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      localStorage.setItem('alvaru_onboarding_complete', 'true');
      onComplete();
      onClose();
    }
  };

  const handleComplete = () => {
    localStorage.setItem('alvaru_onboarding_complete', 'true');
    onComplete();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl bg-card border-border shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center font-bold">Welcome to Alvaru ✨</DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload Base CV */}
        {step === 1 && (
          <div className="space-y-6 py-4">
            <p className="text-center text-muted-foreground">Let's get your first CV ready. This will be your base template.</p>
            <div className="border-2 border-dashed border-border rounded-xl p-10 text-center bg-muted/20 hover:bg-muted/30 transition-colors group">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4 group-hover:text-primary transition-colors" />
              <input type="file" accept=".pdf,.docx" onChange={handleFileUpload} className="hidden" id="cv-upload-onboarding" />
              <label htmlFor="cv-upload-onboarding" className="cursor-pointer inline-flex items-center gap-2 bg-primary text-black font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-lg shadow-lg shadow-primary/20 hover:scale-105 transition-transform">
                Upload your CV
              </label>
              {baseCV && <p className="mt-4 text-sm font-medium text-green-400 flex items-center justify-center gap-2">✅ {baseCV.name}</p>}
              <p className="mt-4 text-[10px] text-muted-foreground uppercase tracking-widest">Supports PDF and DOCX</p>
            </div>
            <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground hover:text-foreground">Skip for now</Button>
          </div>
        )}

        {/* Step 2: Choose Alvaru Mode */}
        {step === 2 && (
          <div className="space-y-6 py-4">
            <p className="text-center text-muted-foreground">How do you want to use Alvaru?</p>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Option A: Extension */}
              <div className="border border-border rounded-xl p-6 text-center space-y-4 bg-muted/10 hover:border-primary/50 transition-all group">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                  <Chrome className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg">Chrome Extension</h3>
                <ul className="text-xs text-left space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2"><span className="text-primary">✓</span> Auto-apply on job boards</li>
                  <li className="flex items-center gap-2"><span className="text-primary">✓</span> One-click CV tailoring</li>
                  <li className="flex items-center gap-2"><span className="text-primary">✓</span> Works on LinkedIn, Indeed</li>
                </ul>
                <Button className="w-full mt-4 bg-primary text-black font-bold" onClick={() => window.open('https://chromewebstore.google.com/detail/alvaru-smart-job-apply/laoahcalgdmfecdbiodloilhcafeoafl', '_blank')}>
                  Add to Chrome
                </Button>
              </div>

              {/* Option B: Website Only */}
              <div className="border border-border rounded-xl p-6 text-center space-y-4 bg-muted/10 hover:border-primary/50 transition-all group">
                <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto group-hover:bg-muted/80 transition-colors">
                  <Globe className="h-8 w-8 text-foreground" />
                </div>
                <h3 className="font-bold text-lg">Website Only</h3>
                <ul className="text-xs text-left space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2"><span>✕</span> No auto-apply</li>
                  <li className="flex items-center gap-2"><span className="text-primary">✓</span> Manual CV tailoring</li>
                  <li className="flex items-center gap-2"><span className="text-primary">✓</span> Works on any device</li>
                </ul>
                <Button variant="outline" className="w-full mt-4 border-border hover:bg-muted font-bold" onClick={handleComplete}>
                  Go to dashboard
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-center text-muted-foreground uppercase tracking-widest opacity-50">You can always install the extension later from settings.</p>
          </div>
        )}

        {/* Step 3: Demo Video */}
        {step === 3 && (
          <div className="space-y-6 py-4">
            <p className="text-center text-muted-foreground">Watch how Alvaru works in 30 seconds</p>
            <div className="bg-black border border-border rounded-xl aspect-video flex items-center justify-center text-muted-foreground overflow-hidden">
              <div className="flex flex-col items-center gap-3">
                <Sparkles className="w-10 h-10 text-primary animate-pulse" />
                <span className="text-[10px] uppercase font-bold tracking-widest">Demo video coming soon</span>
              </div>
            </div>
            <Button onClick={handleComplete} className="w-full bg-primary text-black font-bold h-12 text-md shadow-xl shadow-primary/20">Start using Alvaru</Button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-4">
          {step > 1 && (
            <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} className="text-xs font-bold text-muted-foreground">
              <ChevronLeft className="h-4 w-4 mr-1" /> BACK
            </Button>
          )}
          {step < 3 && step > 1 && (
            <Button variant="ghost" size="sm" onClick={handleSkip} className="ml-auto text-xs font-bold text-muted-foreground">
              SKIP <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
